import { Alert, Button, ButtonGroup, FormHelperText, Link, Grid, Menu, MenuItem, Typography } from "@mui/material";
import { useRef, useState } from "react";
import { EntityValidationStore, UseEntityStore } from "../../common/entityStore";
import { getMostImportantValidationMessage, propBoolField, propCustomEnumField, propDateField, propEnumField, propNumberField, propTextField } from "../../common/entityStoreForm";
import { EnumItemDto, MailProductCode, PaymentMethodId, useDeliveryMethods, useOrderStatesLocalized, usePaymentMethods } from "../../common/enum";
import { InputSizedButtonSx } from "../../common/form";
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map, of } from "rxjs";
import { UseListStore, createListStore } from "../../common/listStore";
import { useStateWithDeps } from "use-state-with-deps";
import { SimpleDialog, useDialog } from "../../common/dialog";
import { renderListStore } from "../../common/listStoreUi";
import { EntityStoreSaveButton } from "../../common/SaveButton";
import { DeliveryPlaceDialog } from "../customer/DeliveryPlaceDialog";
import { round, sortBy } from "lodash";
import { TmOrderDto, TmOrderType, ArticleCategoryDto, ProductDto, canResendOrderConfirmation } from "./common";
import { ArticleAdd } from "./ArticleAdd";
import { DetailQuantity } from "./DetailQuantity";
import { CustomerAddressListDialog } from "./CustomerAddressListDialog";
import { formatNumber, getResource } from "../../common/localization";
import { useDefaultValues } from "../../common/defaultValues";
import { SaleArticleDto, ShipmentDetailDto, showArticleNotAvailableDialog } from "../../common/order";
import shallow from "zustand/shallow";
import { OrderConfirmationDialog } from "./OrderConfirmationDialog";
import { StoreApi, UseBoundStore } from "zustand";
import { DropdownMenuSimple } from "../../common/DropdownMenu";
import { ArrowDropDown } from "@mui/icons-material";
import { ProjectMailOrderConditionDto } from "../../common/types";
import { newId, useRxJsBehaviorSubject, useRxJsSub } from "../../common/utils";
import { SendSmsDialog } from "./SendSmsDialog";

function getText(key: string, defaultMessage?: string) {
    return getResource("Ui.Order." + key, defaultMessage) as string;
}

export function Order({
    useStore,
    useProductsStore,
    useMoCondsStore,
    updateOrder,
    readOnly,
    useArticleCategoriesStore,
    ask,
    useIsClientValidStore,
    product$,
    simpleDialog,
}: {
    useStore: UseEntityStore<TmOrderDto>;
    useProductsStore: UseListStore<ProductDto>;
    useMoCondsStore: UseListStore<ProjectMailOrderConditionDto>;
    updateOrder(updateDiscount?: boolean): void;
    readOnly: boolean;
    useArticleCategoriesStore: UseListStore<ArticleCategoryDto>;
    ask(title: string, message: string | JSX.Element): Promise<boolean>;
    useIsClientValidStore: UseBoundStore<StoreApi<EntityValidationStore<TmOrderDto>>>;
    product$: BehaviorSubject<ProductDto | undefined>;
    simpleDialog: SimpleDialog;
}) {
    const { isTmOrderConfirmationRequired } = useDefaultValues(p => ({ isTmOrderConfirmationRequired: p.clientData.isTmOrderConfirmationRequired }));
    const articleQuantities = useRef<{ [saleArticleId: string]: number | null; }>({});
    const { useStarterArticlesStore, useRegularArticlesStore } = getProductArticlesStores(useProductsStore, useStore);
    const { isTmOrder, isSameCallCenter, tmOrderType, confirmedByCustomer, orderId, isCustomerDeleted, orderStateId, isLead, isLeadPassive } = useStore(p => ({
        isTmOrder: p.clientData.isTmOrder,
        isSameCallCenter: p.clientData.isSameCallCenter,
        tmOrderType: p.clientData.tmOrderType,
        confirmedByCustomer: p.clientData.confirmedByCustomer,
        orderId: p.clientData.id,
        isCustomerDeleted: p.clientData.isCustomerDeleted,
        orderStateId: p.clientData.orderStateId,
        isLead: p.clientData.isLead,
        isLeadPassive: p.clientData.isLeadPassive,
    }));
    const isMail = tmOrderType === TmOrderType.Mail;
    const isCont = tmOrderType === TmOrderType.Continuous;
    const allCategories = useArticleCategoriesStore(p => p.items);
    const [isPaymentMethodCard, setIsPaymentMethodCard] = useState<boolean>(false);
    const [selectedCategoryIndexes, setSelectedCategoryIndexes] = useState<number[]>([0]);
    function getSelectedCategories() {
        let category = allCategories[selectedCategoryIndexes[0]];
        if (category == null) {
            return [];
        }
        const selectedCategories = [category];
        for (let i = 1; i < selectedCategoryIndexes.length; i++) {
            category = category.subCategories[selectedCategoryIndexes[i]];
            selectedCategories.push(category);
        }
        return selectedCategories;
    }
    const selectedCategories = getSelectedCategories();
    const categoryLists = [allCategories, ...selectedCategories.map(p => p.subCategories).filter(p => p.length > 0)];
    const selectedCategory = selectedCategories.at(selectedCategories.length - 1);
    const [useCategoryArticlesStore] = useStateWithDeps(() => createListStore({
        data: of({ items: selectedCategory?.articles ?? [], isFetching: false }),
        listProps: [
            {
                columnKey: "add", label: getText("Articles.Quantity"), width: 100,
                renderCell: params => <ArticleAdd article={params.row}
                    articleQuantities={articleQuantities.current} addArticle={addArticle} />,
            },
            { column: "price", label: getText("Articles.UnitPrice"), width: 90, type: "currency" },
            { column: "code", label: getText("Articles.Code"), width: 60, filter: true, filterWidth: 2 },
            { column: "name", label: getText("Articles.Name"), flex: 1, filter: true, filterWidthFlexGrow: 1 },
        ],
        itemsPerPageMin: 1,
        itemsPerPageMax: 10,
    }), [selectedCategory]);

    const [useDetailsStore] = useState(() => createListStore<ShipmentDetailDto>({
        data: combineLatest([
            useStore.getState().clientData$.pipe(map(p => ({ items: p.data.detailsAndAutos, isFetching: p.isFetching, })), distinctUntilChanged(shallow)),
            useStarterArticlesStore.getState().items$.pipe(map(p => ({ items: p.items.filter(p => !p.isPost).map(p => ({ ...p, isEditable: false })), isFetching: false }))),
        ]).pipe(map(([order, starterArticles]) => ({
            items: [...order.items.filter(p => p.quantity > 0), ...starterArticles.items],
            isFetching: order.isFetching || starterArticles.isFetching,
        }))),
        listProps: [
            {
                column: "quantity", label: getText("Articles.Quantity"), type: "number", width: 100,
                renderCell: params => params.row.isAuto || readOnly || params.row.isEditable === false ? <Typography textAlign="right" pr={1.5} width="100%">{formatNumber(params.row.quantity, useDefaultValues.getState().clientData.landCode, false)}</Typography> :
                    <DetailQuantity useStore={useStore} detail={params.row} ask={ask} useMoCondsStore={useMoCondsStore} updateOrder={() => updateOrder(true)}
                        product$={product$} useArticleCategoriesStore={useArticleCategoriesStore} />
            },
            { column: "price", label: getText("Articles.UnitPrice"), type: "currency", width: 90 },
            { column: "code", label: getText("Articles.Code"), width: 60 },
            { column: "name", label: getText("Articles.OrderArticle"), flex: 1 },
            { columnKey: "totalPrice", label: getText("Articles.TotalPrice"), type: "currency", width: 90, valueGetter: p => round(p.row.price * p.row.quantity, 2) },
        ],
        itemsPerPageMin: 1,
    }));

    const deliveryMethods = useDeliveryMethods(p => p.items);
    const { deliveryMethodId, deliveryMethodPlaceId, detailsError, useDefaultDiscount } = useStore(p => ({
        deliveryMethodId: p.clientData.deliveryMethodId,
        deliveryMethodPlaceId: p.clientData.deliveryMethodPlaceId,
        detailsError: getMostImportantValidationMessage(p, "details").firstMessage,
        useDefaultDiscount: p.clientData.useDefaultDiscount,
    }));
    const deliveryMethod = deliveryMethods.find(p => p.id === deliveryMethodId);
    const deliveryPlaceDialog = useDialog((close, dialogProps) => <DeliveryPlaceDialog close={close} dialogProps={dialogProps} orderId={null}
        deliveryMethodId={deliveryMethodId!} deliveryMethodPlaceId={deliveryMethodPlaceId ?? null}
        changeDeliveryMethodPlace={(id, name, isCoDHandler) => {
            useStore.getState().setClientDataProp("deliveryMethodPlaceId", id);
            useStore.getState().setClientDataProp("deliveryMethodPlaceName", name);
            useStore.getState().setClientDataProp("deliveryMethodPlaceIsCoDHandler", isCoDHandler);

            if (id != null) {
                useStore.getState().setClientDataProp("deliveryAddressId", undefined);
            }
            updateOrder();
        }} getCustomerAddress={() => {
            const order = useStore.getState().clientData;
            return order.deliveryAddressId == null ? order :
                order.customerAddresses.find(p => p.id === order.deliveryAddressId)!;
        }} />, undefined, { usingCustomDialogTag: true });
    async function showDeliveryMethodPlaceDialog() {
        if (useStore.getState().clientData.deliveryMethodId != null) {
            deliveryPlaceDialog.show();
        }
    }

    // same logic is in MO and CO
    useRxJsSub(() => combineLatest([
        useProductsStore.getState().items$,
        useStore.getState().clientData$.pipe(map(p => p.data.tmOrderType === TmOrderType.Mail ? p.data.moProjectId : p.data.projectId), distinctUntilChanged(), filter(p => p != null))
    ]).subscribe(([products, projectId]) => {
        const deliveryMethodId = products.items.find(p => p.projectId === projectId)?.deliveryMethodId ?? null;
        if (deliveryMethodId != null && deliveryMethodId !== useStore.getState().clientData.deliveryMethodId) {
            useStore.getState().setServerDataProp("deliveryMethodId", deliveryMethodId);
            useStore.getState().setClientDataProp("deliveryMethodId", deliveryMethodId);
            useStore.getState().setClientDataProp("deliveryMethodPlaceId", null);
            useStore.getState().setClientDataProp("deliveryMethodPlaceName", null);
        }
    }));

    const [useCustomerAddressListStore] = useState(() => createListStore<EnumItemDto>({
        data: useStore.getState().clientData$.pipe(
            map(p => ({ customerAddresses: p.data.customerAddresses, isFetching: p.isFetching })),
            distinctUntilChanged(shallow),
            map(p => ({
                items: sortBy(p.customerAddresses, p => p.id).map(p => ({
                    id: p.id,
                    name: [p.fname, p.lname, p.coadr, p.strt1 + " " + p.strt2, p.zipCode, p.city].filter(p => p.trim().length > 0).join(", "),
                })), isFetching: p.isFetching,
            }))),
    }));
    const addressListDialog = useDialog(close => <CustomerAddressListDialog customItemSelect={item => useStore.getState().setClientDataProp("deliveryAddressId", item.id)}
        close={close} useCustomerStore={useStore} disabled={false} canManageExisting={false} />, "md");
    async function addArticle(article: SaleArticleDto, quantity: number) {
        if (!article.isAvailable) {
            showArticleNotAvailableDialog(simpleDialog);
            return;
        }
        const details = useStore.getState().clientData.details?.slice() ?? [];
        const index = details.findIndex(p => p.saleArticleId === article.id && p.price === article.price);
        if (quantity > 9 && !await ask(getText("AddArticle.Title"), <>{getText("AddArticle.Content").replaceAll("{0}", formatNumber(quantity, useDefaultValues.getState().clientData.landCode, false))}</>)) {
            return;
        }
        if (index >= 0) {
            const detail = { ...details[index] };
            if (detail.quantity + quantity > 9 && !await ask(getText("ChangeArticle.Title"), <>{getText("ChangeArticle.Content").replaceAll("{0}", formatNumber(detail.quantity + quantity, useDefaultValues.getState().clientData.landCode, false))}</>)) {
                return;
            }
            detail.quantity += quantity;
            details[index] = detail;
        } else {
            details.push({
                id: newId(),
                saleArticleId: article.id,
                baseQuantity: article.baseQuantity,
                code: article.code,
                name: article.name,
                price: article.price,
                isPost: false,
                weight: article.weight,
                quantity,
                isAuto: false,
                isAvailable: article.isAvailable,
            });
        }
        useStore.getState().setClientDataProp("details", details);
        useStore.getState().setClientDataProp("giftsNotSelectedOk", undefined);
        updateOrder();
    }
    const orderConfirmationDialog = useDialog(close => <OrderConfirmationDialog close={close} orderId={orderId!} />);
    const isClientValid = useIsClientValidStore(p => p.isClientValid);
    const isSaving = useStore(p => p.isSaving);
    const isFetching = useStore(p => p.isFetching);
    const [useProjectPaymentMethods] = useState(() => createListStore<EnumItemDto>({
        data: combineLatest([
            product$,
            usePaymentMethods.getState().items$,
        ]).pipe(map(([product, paymentMethods]) => {
            return {
                items: readOnly ? paymentMethods.items : product == null ? [] : product.paymentMethodIds.length === 0 ? paymentMethods.items :
                    paymentMethods.items.map(p => ({ ...p, inactive: p.inactive || !product.paymentMethodIds.includes(p.id as PaymentMethodId) })),
                isFetching: paymentMethods.isFetching,
            };
        })),
    }));

    const sendSmsDialog = useDialog((close, dialogProps) => <SendSmsDialog close={close} dialogProps={dialogProps}
        useStore={useStore} product$={product$} simpleDialog={simpleDialog} />, undefined, { usingCustomDialogTag: true });
    const hasSmsTemplates = (useRxJsBehaviorSubject(product$)?.tmSmsTemplates?.length ?? 0) > 0;

    const canResendOrderConfirmationValue =
        isTmOrder &&
        isSameCallCenter &&
        orderId != null &&
        isTmOrderConfirmationRequired &&
        !confirmedByCustomer &&
        !isCustomerDeleted &&
        orderStateId != null &&
        canResendOrderConfirmation(orderStateId);

    return <>
        {deliveryPlaceDialog.render()}
        {addressListDialog.render()}
        {orderConfirmationDialog.render()}
        {sendSmsDialog.render()}
        <Grid item xs={12} container columnSpacing={2}>
            <Grid item container rowSpacing={2} mb={2}>
                {isCustomerDeleted && <Grid item xs={12}><Alert severity="error">{getText("CustomerIsDeleted")}</Alert></Grid>}
                {confirmedByCustomer === false && isTmOrder && isSameCallCenter && isTmOrderConfirmationRequired && <Grid item xs={12}><Alert severity="warning">{getText("IsUnconfirmedByCustomer")}</Alert></Grid>}
                {confirmedByCustomer === true && isTmOrderConfirmationRequired && <Grid item xs={12}><Alert severity="success">{getText("IsConfirmedByCustomer")}</Alert></Grid>}
            </Grid>
            {!isMail && !readOnly && <Grid item xs={12}>{propCustomEnumField(useStore)("projectId", getText("Product"), useProductsStore, {
                filter: p => p.code !== MailProductCode,
                getId: p => p.projectId,
                getName: p => p.name,
                autoComplete: true,
                onChange: () => {
                    const product = product$.value;
                    const paymentMethodId = useStore.getState().clientData.paymentMethodId;
                    if (paymentMethodId != null) {
                        const shouldResetPaymentMethod = product == null ||
                            (product.paymentMethodIds.length !== 0 && !product.paymentMethodIds.includes(paymentMethodId));
                        if (shouldResetPaymentMethod) {
                            useStore.getState().setClientDataProp("paymentMethodId", product?.paymentMethodId);
                        }
                    }
                    updateOrder();
                }
            })}</Grid>}
            {!isMail && readOnly && <Grid item xs={12}>{propTextField(useStore)("productName", getText("Product"), { disabled: true })}</Grid>}
            {!isCont && !readOnly && <Grid item xs={12} sx={{ m: -1 }}>
                {categoryLists[0].map((p, i) => <ButtonGroup sx={{ m: 1 }} key={i}>
                    <Button variant={selectedCategoryIndexes[0] === i && selectedCategoryIndexes.length === 1 ? "contained" : "outlined"} sx={{ textTransform: "none" }}
                        onClick={() => setSelectedCategoryIndexes([i])}>
                        {localizeCategoryName(p.name, 0)}
                    </Button>
                    {p.subCategories.length > 0 && <DropdownMenuSimple
                        triggerElement={onClick => <Button onClick={onClick} size="small" variant={selectedCategoryIndexes[0] === i && selectedCategoryIndexes.length > 1 ? "contained" : "outlined"}><ArrowDropDown /></Button>}
                        menu={(menuProps, close) => <Menu {...menuProps}>
                            {p.subCategories.map((r, j) => <MenuItem key={j} selected={selectedCategoryIndexes[0] === i && selectedCategoryIndexes[1] === j} onClick={() => {
                                setSelectedCategoryIndexes([i, j]);
                                close();
                            }}>{r.name}</MenuItem>)}
                        </Menu>} />}
                </ButtonGroup>)}
            </Grid>}
            {!isCont && !readOnly && <Grid item xs={12} mt={2} mb={2}>
                <Typography component="span" fontSize={18}>{getText("ArticleCategory").replaceAll("{0}", selectedCategories.map((p, i) => localizeCategoryName(p.name, i)).join(" / "))}</Typography>
                {renderListStore(useCategoryArticlesStore, {
                    instantSearch: true,
                    hideFooter: true,
                    rowHeight: 30,
                    onClick(item) {
                        addArticle(item, articleQuantities.current[item.id] ?? item.baseQuantity);
                    },
                    getRowClass: p => p.isAvailable ? [] : ["redRibbon"],
                })}</Grid>}
            {!isCont && <Grid item xs={12} mb={2}>
                <Typography component="span" fontSize={18}>{getText("OrderArticles")}</Typography>
                {renderListStore(useDetailsStore, {
                    hideFooter: true,
                    rowHeight: 30,
                })}
                {detailsError && <FormHelperText error>{detailsError}</FormHelperText>}
            </Grid>}
            {isCont && <Grid item xs={12} mt={2} mb={2}>
                <Typography component="span" fontSize={18}>{getText("StarterArticles")}</Typography>
                {renderListStore(useStarterArticlesStore, { hideFooter: true, rowHeight: 30 })}</Grid>}
            {!isMail && <Grid item xs={12} mt={2} mb={2}>
                <Typography component="span" fontSize={18}>{getText("RegularArticles")}</Typography>
                {renderListStore(useRegularArticlesStore, { hideFooter: true, rowHeight: 30 })}</Grid>}
            <Grid item xs={12} mt={2} mb={2}></Grid>
            {deliveryMethod?.hasPlaces && <Grid item container xs={12} spacing={2}>
                <Grid item xs>{propTextField(useStore)("deliveryMethodPlaceName", getText("ParcelShop").replaceAll("{0}", deliveryMethod?.name ?? ""), { readOnly: true, disabled: readOnly })}</Grid>
                {!readOnly && <Grid item><Button disabled={deliveryMethod == null ? true : !deliveryMethod.hasPlaces} sx={InputSizedButtonSx} onClick={showDeliveryMethodPlaceDialog}>{getText("ParcelShop.Change")}</Button></Grid>}
            </Grid>}
            <Grid item container xs={12} spacing={2}>
                <Grid item xs zeroMinWidth>{propEnumField(useStore)("deliveryAddressId", getText("DeliveryAddress"), useCustomerAddressListStore,
                    { autoComplete: false, addNone: true, disabled: readOnly || deliveryMethodPlaceId != null, noneText: getText("DefaultAddress", "(default)") })}</Grid>
                {!readOnly && <Grid item><Button sx={InputSizedButtonSx} disabled={deliveryMethodPlaceId != null} onClick={() => addressListDialog.show()}>{getText("DeliveryAddress.Manage")}</Button></Grid>}
            </Grid>
            <Grid item xs={6}>{propEnumField(useStore)("paymentMethodId", getText("PaymentMethod"), useProjectPaymentMethods, {
                disabled: readOnly || tmOrderType === TmOrderType.Continuous,
                onChange: () => {
                    const paymentMethodId = useStore.getState().clientData.paymentMethodId;
                    setIsPaymentMethodCard(paymentMethodId === PaymentMethodId.Card);
                }
            })}</Grid>
            {isPaymentMethodCard &&
                <Grid item container xs={6} alignItems="center">
                    {<Grid item><EntityStoreSaveButton useStore={useStore} text={getText("OnlinePayment")} disableEnter customSave={async save => {
                        useStore.getState().setClientDataProp("createPaymentLink", true);
                        save(false);
                    }} /></Grid>}
                </Grid>
            }
            <Grid item xs={6}>{propDateField(useStore)("minShpDte", "minShpDteFormatted", getText("MinShpDate"), { disabled: readOnly })}</Grid>
            <Grid item xs={6}>{propNumberField(useStore)("articlePrice", null, getText("PriceArticles"), { isCurrency: true, disabled: true })}</Grid>
            <Grid item xs={6}>{propNumberField(useStore)("postagePrice", null, getText("PricePostage"), { isCurrency: true, disabled: true })}</Grid>
            {!isCont && <Grid item xs={6}>{propNumberField(useStore)("discAmt", "discAmtFormatted", getText("PriceDiscount"), { isCurrency: true, disabled: useDefaultDiscount || readOnly, onChange: () => updateOrder(false) })}</Grid>}
            <Grid item xs={6}>{propNumberField(useStore)("toPay", null, getText("PriceToPay"), { isCurrency: true, disabled: true })}</Grid>
            {orderId != null && <Grid item xs={6}>{propEnumField(useStore)("orderStateId", getText("OrderState"), useOrderStatesLocalized, { disabled: true })}</Grid>}
            {isTmOrderConfirmationRequired && <Grid item xs={6}>{propBoolField(useStore)("affidavit", getText("Affidavit"), { disabled: readOnly })}</Grid>}

            <Grid item xs={12} mt={2} mb={2} justifyContent="end" container spacing={2}>
                {hasSmsTemplates && <Grid item><Button onClick={() => sendSmsDialog.show()}>{getResource("Ui.Tm.SendSms")}</Button></Grid>}
                {!readOnly && <Grid item><EntityStoreSaveButton useStore={useStore} text={getText("CustomerNotInterested")} color="warning" disableEnter forceEnabled={(isClientValid || (isLead && !isLeadPassive)) && !isSaving && !isFetching && !orderId} customSave={async save => {
                    useStore.getState().setClientDataProp("notInterested", true);
                    save(true);
                }} /></Grid>}
                {!readOnly && !isPaymentMethodCard && <Grid item><EntityStoreSaveButton useStore={useStore} text={getText("CreateOrder")} disableEnter customSave={async save => {
                    useStore.getState().setClientDataProp("notInterested", false);
                    save(false);
                }} /></Grid>}
                {canResendOrderConfirmationValue && <Grid item>
                    <Button color="edit" onClick={() => orderConfirmationDialog.show()}>{getText("ResendConfirmationRequest")}</Button>
                </Grid>}
            </Grid>
        </Grid>
    </>;
}

function getProductArticlesStores(useProductsStore: UseListStore<ProductDto>, useStore: UseEntityStore<TmOrderDto>) {
    const [useStarterArticlesStore] = useState(() => createListStore<ShipmentDetailDto>({
        data: useStore.getState().clientData$.pipe(map(p => ({ starterArticles: p.data.starterArticles, isFetching: p.isFetching })), distinctUntilChanged(shallow))
            .pipe(map(p => ({ items: p.starterArticles ?? [], isFetching: p.isFetching }))),
        getId: p => p.saleArticleId,
        listProps: [
            { column: "quantity", label: getText("Articles.Quantity"), type: "number", width: 70 },
            { column: "price", label: getText("Articles.UnitPrice"), type: "currency", width: 90 },
            { column: "code", label: getText("Articles.Code"), width: 60 },
            { column: "name", label: getText("Articles.Name"), flex: 1 },
            { columnKey: "totalPrice", label: getText("Articles.TotalPrice"), type: "currency", width: 90, valueGetter: p => round(p.row.price * p.row.quantity, 2) },
        ],
        itemsPerPageMin: 1,
    }));
    const [useRegularArticlesStore] = useState(() => createListStore<ShipmentDetailDto>({
        data: combineLatest([
            useProductsStore.getState().items$,
            useStore.getState().clientData$.pipe(map(p => p.data.projectId), distinctUntilChanged()),
            useStore.getState().clientData$.pipe(map(p => p.data.regularArticles), distinctUntilChanged()),
        ]).pipe(map(([products, projectId, regularArticles]) => ({
            items: (products.items.find(p => p.projectId === projectId)?.regularArticles ?? [])
                .concat(regularArticles ?? []),
            isFetching: products.isFetching
        }))),
        getId: p => p.saleArticleId,
        listProps: [
            { column: "quantity", label: getText("Articles.Quantity"), type: "number", width: 70 },
            { column: "price", label: getText("Articles.UnitPrice"), type: "currency", width: 90 },
            { column: "code", label: getText("Articles.Code"), width: 60 },
            { column: "name", label: getText("Articles.Name"), flex: 1 },
            { columnKey: "totalPrice", label: getText("Articles.TotalPrice"), type: "currency", width: 90, valueGetter: p => round(p.row.price * p.row.quantity, 2) },
        ],
        itemsPerPageMin: 1,
    }));

    return { useStarterArticlesStore, useRegularArticlesStore };
}

function localizeCategoryName(name: string, level: number) {
    return level === 0 && name === "All articles" ? getText("AllArticles", name) : name;
}